import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

/**
 * Skeleton Component
 *
 * Loading placeholder components with shimmer animation
 * for content that is being loaded.
 *
 * @example
 * <Skeleton className="h-4 w-32" />
 * <Skeleton.Text lines={3} />
 * <Skeleton.Avatar size="lg" />
 * <Skeleton.Card />
 */

const Skeleton = forwardRef(
  (
    {
      className,
      animate = true,
      rounded = 'md', // 'none', 'sm', 'md', 'lg', 'full'
      ...props
    },
    ref
  ) => {
    const roundedClasses = {
      none: 'rounded-none',
      sm: 'rounded',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      full: 'rounded-full',
    };

    return (
      <div
        ref={ref}
        role="status"
        aria-label="Loading"
        className={clsx(
          'bg-gray-200',
          animate && 'skeleton-shimmer',
          roundedClasses[rounded],
          className
        )}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

/**
 * Skeleton Text
 * Multiple lines of text placeholder
 */
const SkeletonText = forwardRef(
  (
    {
      lines = 3,
      lastLineWidth = '60%',
      spacing = 'normal', // 'tight', 'normal', 'loose'
      className,
      ...props
    },
    ref
  ) => {
    const spacingClasses = {
      tight: 'space-y-2',
      normal: 'space-y-3',
      loose: 'space-y-4',
    };

    return (
      <div ref={ref} className={clsx(spacingClasses[spacing], className)} {...props}>
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton
            key={index}
            className="h-4"
            style={{
              width: index === lines - 1 ? lastLineWidth : '100%',
            }}
          />
        ))}
      </div>
    );
  }
);

SkeletonText.displayName = 'SkeletonText';

/**
 * Skeleton Avatar
 * Circular avatar placeholder
 */
const SkeletonAvatar = forwardRef(
  (
    {
      size = 'md', // 'xs', 'sm', 'md', 'lg', 'xl'
      className,
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      xs: 'size-6',
      sm: 'size-8',
      md: 'size-10',
      lg: 'size-12',
      xl: 'size-16',
      '2xl': 'size-20',
    };

    return (
      <Skeleton
        ref={ref}
        rounded="full"
        className={clsx(sizeClasses[size], className)}
        {...props}
      />
    );
  }
);

SkeletonAvatar.displayName = 'SkeletonAvatar';

/**
 * Skeleton Button
 * Button placeholder
 */
const SkeletonButton = forwardRef(
  (
    {
      size = 'md', // 'sm', 'md', 'lg'
      width = 'auto', // 'auto', 'full'
      className,
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'h-8 w-20',
      md: 'h-10 w-24',
      lg: 'h-12 w-32',
    };

    return (
      <Skeleton
        ref={ref}
        rounded="lg"
        className={clsx(
          sizeClasses[size],
          width === 'full' && 'w-full',
          className
        )}
        {...props}
      />
    );
  }
);

SkeletonButton.displayName = 'SkeletonButton';

/**
 * Skeleton Input
 * Form input placeholder
 */
const SkeletonInput = forwardRef(
  (
    {
      withLabel = true,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div ref={ref} className={clsx('space-y-2', className)} {...props}>
        {withLabel && <Skeleton className="h-4 w-24" />}
        <Skeleton className="h-10 w-full" rounded="lg" />
      </div>
    );
  }
);

SkeletonInput.displayName = 'SkeletonInput';

/**
 * Skeleton Card
 * Card content placeholder
 */
const SkeletonCard = forwardRef(
  (
    {
      hasImage = false,
      imageHeight = 'h-40',
      hasHeader = true,
      hasFooter = false,
      lines = 3,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'bg-white rounded-xl border border-gray-100 shadow-card overflow-hidden',
          className
        )}
        {...props}
      >
        {/* Image placeholder */}
        {hasImage && (
          <Skeleton rounded="none" className={clsx('w-full', imageHeight)} />
        )}

        <div className="p-5 space-y-4">
          {/* Header placeholder */}
          {hasHeader && (
            <div className="flex items-center gap-3">
              <SkeletonAvatar size="md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          )}

          {/* Content placeholder */}
          <SkeletonText lines={lines} />
        </div>

        {/* Footer placeholder */}
        {hasFooter && (
          <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
            <SkeletonButton size="sm" />
            <SkeletonButton size="sm" />
          </div>
        )}
      </div>
    );
  }
);

SkeletonCard.displayName = 'SkeletonCard';

/**
 * Skeleton Table Row
 * Table row placeholder
 */
const SkeletonTableRow = forwardRef(
  (
    {
      columns = 4,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <tr ref={ref} className={className} {...props}>
        {Array.from({ length: columns }).map((_, index) => (
          <td key={index} className="px-4 py-3">
            <Skeleton
              className="h-4"
              style={{
                width: `${60 + Math.random() * 40}%`,
              }}
            />
          </td>
        ))}
      </tr>
    );
  }
);

SkeletonTableRow.displayName = 'SkeletonTableRow';

/**
 * Skeleton Table
 * Full table placeholder
 */
const SkeletonTable = forwardRef(
  (
    {
      rows = 5,
      columns = 4,
      hasHeader = true,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'bg-white rounded-xl border border-gray-100 shadow-card overflow-hidden',
          className
        )}
        {...props}
      >
        <table className="min-w-full divide-y divide-gray-100">
          {hasHeader && (
            <thead className="bg-gray-50">
              <tr>
                {Array.from({ length: columns }).map((_, index) => (
                  <th key={index} className="px-4 py-3 text-left">
                    <Skeleton className="h-3 w-20" />
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="divide-y divide-gray-100">
            {Array.from({ length: rows }).map((_, index) => (
              <SkeletonTableRow key={index} columns={columns} />
            ))}
          </tbody>
        </table>
      </div>
    );
  }
);

SkeletonTable.displayName = 'SkeletonTable';

/**
 * Skeleton List Item
 * List item placeholder with avatar
 */
const SkeletonListItem = forwardRef(
  (
    {
      hasAvatar = true,
      hasAction = false,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={clsx('flex items-center gap-4 p-4', className)}
        {...props}
      >
        {hasAvatar && <SkeletonAvatar size="md" />}
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-32" />
        </div>
        {hasAction && <SkeletonButton size="sm" />}
      </div>
    );
  }
);

SkeletonListItem.displayName = 'SkeletonListItem';

/**
 * Skeleton List
 * Full list placeholder
 */
const SkeletonList = forwardRef(
  (
    {
      items = 5,
      hasAvatar = true,
      hasAction = false,
      divided = true,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'bg-white rounded-xl border border-gray-100 shadow-card',
          divided && 'divide-y divide-gray-100',
          className
        )}
        {...props}
      >
        {Array.from({ length: items }).map((_, index) => (
          <SkeletonListItem
            key={index}
            hasAvatar={hasAvatar}
            hasAction={hasAction}
          />
        ))}
      </div>
    );
  }
);

SkeletonList.displayName = 'SkeletonList';

/**
 * Skeleton Stat Card
 * Dashboard stat card placeholder
 */
const SkeletonStatCard = forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'bg-white rounded-xl border border-gray-100 shadow-card p-5',
          className
        )}
        {...props}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="size-12" rounded="xl" />
        </div>
      </div>
    );
  }
);

SkeletonStatCard.displayName = 'SkeletonStatCard';

/**
 * Skeleton Dashboard
 * Complete dashboard layout placeholder
 */
const SkeletonDashboard = forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('space-y-6', className)} {...props}>
        {/* Stats row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </div>

        {/* Content row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SkeletonCard hasImage={false} lines={4} />
          </div>
          <div>
            <SkeletonList items={4} hasAction={false} />
          </div>
        </div>
      </div>
    );
  }
);

SkeletonDashboard.displayName = 'SkeletonDashboard';

// Attach sub-components
Skeleton.Text = SkeletonText;
Skeleton.Avatar = SkeletonAvatar;
Skeleton.Button = SkeletonButton;
Skeleton.Input = SkeletonInput;
Skeleton.Card = SkeletonCard;
Skeleton.Table = SkeletonTable;
Skeleton.TableRow = SkeletonTableRow;
Skeleton.List = SkeletonList;
Skeleton.ListItem = SkeletonListItem;
Skeleton.StatCard = SkeletonStatCard;
Skeleton.Dashboard = SkeletonDashboard;

export {
  Skeleton,
  SkeletonText,
  SkeletonAvatar,
  SkeletonButton,
  SkeletonInput,
  SkeletonCard,
  SkeletonTable,
  SkeletonTableRow,
  SkeletonList,
  SkeletonListItem,
  SkeletonStatCard,
  SkeletonDashboard,
};
export default Skeleton;
